/**
 * THE PAINT-BY-NUMBERS PATHWAY — one record, one next step.
 *
 * Every other module in this space answers "what is true about this
 * organization". This one answers the only question a rep actually has in front
 * of a list of 26,000 fire departments: **what do I do about THIS one, right
 * now.** Pure JS, no React, so `verify-spaces.mjs` asserts the pathway directly
 * rather than through a rendered component.
 *
 * THE RULES THAT MAKE IT PAINT-BY-NUMBERS:
 *
 *  1. **Exactly ONE step is ever shown.** The record's own state picks it. A
 *     screen that shows three possible moves is the data-staring problem with
 *     extra buttons on it.
 *  2. **The step is NUMBERED against a fixed total** (`step 1 of 2`), so the rep
 *     can see where this record sits in a sequence they'll recognize on the next
 *     one. The sequence is the same for every record in the space.
 *  3. **Every step says what happens next**, in one line, before the click.
 *  4. **A finished record says it is finished** and offers no busywork.
 *  5. **Nothing here writes.** A step either opens the create flow (which
 *     writes, through the guarded backend route) or points at the human action
 *     in HubSpot. The Command Center still proposes; a human still decides.
 *
 * The pathways:
 *
 *   COMMUNITY HERO (police · fire)   1 Nominate → 2 Advance / produce
 *   GREEK PURSUIT  (greek)           1 Start → 2 Assign an owner → 3 Mark won
 *   CHEER PARTNERSHIP (cheer)        1 Start the pursuit → 2 Get the contract
 *                                    signed → 3 Launch the exclusive branded
 *                                    jewelry program → 4 Work a team's title
 */

import {
  COMMUNITY_HERO, GREEK_PURSUIT, heroMeta, pursuitDealMeta,
} from './recognition.js'
import { CHEER_PARTNERSHIP, partnershipMeta } from './cheer.js'

/* ⚠️ A RAW ID IS NOT A STAGE NAME.
 *
 * Found by using the live board: a cheer card read "Opened Aug 6, 2026 · nobody
 * on it · currently 1413640334." That number is HubSpot's internal `dealstage`
 * id, unresolved — the backend hands the id through when it cannot map it to a
 * label, and the copy printed it verbatim into a sentence a rep reads.
 *
 * The real fix is server-side (resolve dealstage → label, as the owner read
 * already resolves owner ids to names). But a raw identifier must never reach
 * customer-facing copy whatever the backend does, so the guard belongs here
 * too: an all-digit or id-shaped "stage" is DROPPED rather than printed. Saying
 * less is always better than saying a number nobody can read. */
const ID_SHAPED = /^[0-9]{5,}$/
export function stageWords(v) {
  const s = String(v ?? '').trim()
  if (!s || ID_SHAPED.test(s)) return ''
  return s
}


/* ---- The HubSpot deep link ------------------------------------------ */

/**
 * The SR portal — confirmed in the backend's own pursuit mapping. A step whose
 * real action happens in HubSpot (approving a nomination, marking a pursuit
 * won) links straight to the deal rather than describing where to find it.
 *
 * Overridable, and the link is simply ABSENT if the id is cleared — a dead
 * HubSpot link is worse than no link.
 */
export const HUBSPOT_PORTAL_ID =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_HUBSPOT_PORTAL_ID) || '51263144'

/** The record page for one deal — '' when we can't build a real one. */
export function hubspotDealUrl(dealId) {
  const id = String(dealId || '').trim()
  if (!id || !/^\d+$/.test(id)) return ''
  if (!HUBSPOT_PORTAL_ID) return ''
  return `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-3/${id}`
}

/* ---- helpers -------------------------------------------------------- */

const fmt = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : ''

/** The hero has been handed over — the program is complete for this department. */
function heroIsPresented(meta) {
  return meta.present && meta.key === 'presented'
}

/* ---- the two pathways ----------------------------------------------- */

export const HERO_PATHWAY = ['nominate', 'advance']
export const GREEK_PATHWAY = ['start', 'own', 'win']
/**
 * THE CHEER PATHWAY — four steps, because a cheer partnership is genuinely two
 * things (a signed contract AND an exclusive branded jewelry program) and the
 * championship engine only starts once both are done.
 */
export const CHEER_PATHWAY = ['pursue', 'contract', 'jewelry', 'championships']

/**
 * The ONE next step for a Community Hero record.
 * @param {object} record  an adapted pursuit record
 * @param {object} [opts]  `{ heroLabel, noun, honoreeNoun }` from the registry
 */
function heroStep(record, opts = {}) {
  const heroLabel = opts.heroLabel || 'Community Hero'
  const noun = opts.noun || 'department'
  const honoree = opts.honoreeNoun || 'honoree'
  const meta = heroMeta(record)
  const of = HERO_PATHWAY.length

  /* DONE. The piece was presented — there is nothing to chase here. */
  if (heroIsPresented(meta)) {
    return {
      key: 'hero-presented', step: of, of, done: true, tone: 'done',
      title: `${meta.name || 'The honoree'} was presented`,
      what: `This ${noun} is complete. Nothing to do — it stays on the board as a reference.`,
      cta: null,
    }
  }

  /* STEP 2. A live nomination: the move is to advance it into production. */
  if (meta.present && meta.active) {
    const who = meta.name || `this ${noun}'s ${honoree}`
    const nominated = fmt(record?.hero?.nominatedAt)
    return {
      key: 'hero-advance', step: 2, of, done: false, tone: 'active',
      title: `Advance ${who} — produce the recognition piece`,
      what: [
        nominated ? `Nominated ${nominated}` : 'Nomination open',
        `currently ${(stageWords(meta.label) || 'open').toLowerCase()}`,
      ].join(' · ') + `. Next: approve the ${honoree} and move the nomination into production so the ring or pendant gets made.`,
      cta: { label: 'Advance the nomination', action: 'advance', dealId: record?.hero?.id || '' },
    }
  }

  /* STEP 1. No live nomination — including one that was declined or withdrawn,
     which frees the slot and puts the department right back at the start. */
  const reopened = meta.present && !meta.active
  return {
    key: 'hero-nominate', step: 1, of, done: false, tone: reopened ? 'reopened' : 'gap',
    title: `Nominate this ${noun}'s ${heroLabel}`,
    what: reopened
      ? `The previous nomination is ${String(meta.label || '').toLowerCase()}, so the slot is open again. SR provides the ring or pendant at NO COST — pick the ${honoree} and their years of service.`
      : `SR provides the ring or pendant at NO COST. Name the ${honoree} and their years of service; that files the nomination against this ${noun} and puts it in the recognition pipeline.`,
    cta: { label: `Nominate ${heroLabel}`, action: 'nominate' },
  }
}

/** The ONE next step for a Greek pursuit record. */
function greekStep(record) {
  const meta = pursuitDealMeta(record)
  const deal = record?.pursuitDeal
  const of = GREEK_PATHWAY.length
  const product = record?.product || deal?.product || 'a product'

  /* DONE. Won — and only ever from a genuinely won deal. */
  if (meta.present && meta.won) {
    const when = fmt(record?.wonDate)
    return {
      key: 'greek-won', step: of, of, done: true, tone: 'done',
      title: `${product} — won${when ? ` ${when}` : ''}`,
      what: 'Closed won. Hand it to production; nothing further is needed on the board.',
      cta: null,
    }
  }

  /* STEP 1. Nothing live here — a lost pursuit puts the org back at the start,
     because the next move is to go again, not to stare at the loss. */
  if (!meta.present || meta.lost) {
    return {
      key: 'greek-start', step: 1, of, done: false, tone: meta.lost ? 'reopened' : 'gap',
      title: 'Start a pursuit — pick the product',
      what: meta.lost
        ? `The last pursuit here closed lost. Open a new one against a different product and it lands on this organization's board.`
        : 'Choose what SR is going after at this organization — badges, chapter rings, officer recognition. The product IS the deal, so the board reads as the list of things we are actually pursuing.',
      cta: { label: 'Start pursuit', action: 'pursue' },
    }
  }

  const owner = String(record?.rep || deal?.owner || '').trim()
  const opened = fmt(record?.pursuitDate)

  /* STEP 2. Live, but nobody is working it. That is the blocker, so it is the
     step — a pursuit with no owner does not advance on its own. */
  if (!owner) {
    return {
      key: 'greek-own', step: 2, of, done: false, tone: 'gap',
      title: `Assign a rep to ${product}`,
      what: `${opened ? `Opened ${opened}` : 'Open'} with nobody on it. A pursuit with no rep does not move — put a rep's name on the deal and it enters the working set.`,
      cta: { label: 'Assign a rep', action: 'assign', dealId: deal?.id || '' },
    }
  }

  /* STEP 3. Owned and live — the only thing left is the win. */
  return {
    key: 'greek-win', step: 3, of, done: false, tone: 'active',
    title: `Work ${product} to won`,
    what: `${owner}${opened ? ` · opened ${opened}` : ''} · ${meta.label || 'in pursuit'}. When it lands, record the win on the deal — the board reads the won date straight back from HubSpot.`,
    cta: { label: 'Mark won', action: 'win', dealId: deal?.id || '' },
  }
}

/**
 * The ONE next step for a CHEER GYM.
 *
 * The gym's own partnership state picks it, and the sequence is the partnership
 * definition made walkable: you cannot launch a jewelry program without a
 * contract, and you are not working a gym's championships until it is a partner.
 * Step 4 is not a "done" state — it is the ONGOING engine, which is the whole
 * reason the partnership is worth signing.
 */
function cheerStep(record, opts = {}) {
  const noun = opts.noun || 'gym'
  const meta = partnershipMeta(record)
  const of = CHEER_PATHWAY.length
  const titles = Number(record?.titleCount) || 0
  const teams = Number(record?.teamCount) || 0
  const deal = record?.partnershipDeal

  /* STEP 4. A partner — both halves done. The move is now the revenue engine:
     the gym's teams and their championship pursuits. */
  if (meta.partner) {
    const footprint = titles
      ? `${titles} championship relationship${titles === 1 ? '' : 's'} across ${teams} team${teams === 1 ? '' : 's'}`
      : 'no championship titles matched to this gym yet'
    return {
      key: 'cheer-championships', step: 4, of, done: false, tone: 'active',
      title: `Advance a team's championship pursuit`,
      what: `Partner — contract signed and the exclusive branded jewelry program is live. ${footprint}. Next: work the gym's teams — every title is a ring order, and a partner gym's teams are ours to pursue every season.`,
      cta: { label: titles ? `Open ${teams} team${teams === 1 ? '' : 's'}` : 'Open the gym', action: 'championships' },
    }
  }

  /* STEP 3. Signed, but the exclusive program is not live. That is the half of
     the partnership that actually reaches the athletes. */
  if (meta.signed) {
    const j = meta.jewelry
    return {
      key: 'cheer-jewelry', step: 3, of, done: false, tone: 'gap',
      title: 'Launch the exclusive branded jewelry program',
      what: `The contract is signed${meta.signedAt ? ` (${fmt(meta.signedAt)})` : ''} but the jewelry program is ${j.present ? j.label.toLowerCase() : 'not launched'}. A partnership is both halves — build the gym-branded, exclusive collection and this ${noun} becomes a partner.`,
      cta: { label: 'Launch the program', action: 'jewelry', dealId: deal?.id || '' },
    }
  }

  /* STEP 2. A live pursuit with no signature. The contract IS the blocker. */
  if (meta.key === 'pursuit') {
    const owner = String(record?.rep || deal?.owner || '').trim()
    return {
      key: 'cheer-contract', step: 2, of, done: false, tone: 'active',
      title: 'Get the partnership contract signed',
      what: [
        meta.openedAt ? `Opened ${fmt(meta.openedAt)}` : 'Pursuit open',
        owner ? `owned by ${owner}` : 'nobody on it',
        stageWords(meta.stageLabel) ? `currently ${stageWords(meta.stageLabel).toLowerCase()}` : '',
      ].filter(Boolean).join(' · ') + '. Next: get the paper signed on the deal in HubSpot — that unlocks the exclusive jewelry program.',
      cta: { label: 'Open the deal', action: 'contract', dealId: deal?.id || '' },
    }
  }

  /* STEP 1. A TARGET — including a gym whose last pursuit was lost, because the
     move there is to go again, not to stare at the loss. */
  const footprint = titles
    ? ` This gym already has ${titles} championship relationship${titles === 1 ? '' : 's'} across ${teams} team${teams === 1 ? '' : 's'} — it is winning without us.`
    : ''
  return {
    key: 'cheer-pursue', step: 1, of, done: false, tone: meta.reopened ? 'reopened' : 'gap',
    title: 'Start the partnership pursuit',
    what: (meta.reopened
      ? `The last partnership pursuit here closed lost, so the ${noun} is back in the TAM.`
      : `No partnership pursuit is open at this ${noun}.`) +
      `${footprint} Opening one files a partnership deal against the gym and puts it on the pipeline toward a signed contract and an exclusive branded jewelry program.`,
    cta: { label: 'Start partnership pursuit', action: 'pursue' },
  }
}

/**
 * THE NEXT STEP for one record — or `null` in a space that has no pathway.
 *
 * `kind` comes off the record itself (set by the adapter from the line's
 * config), so the Championships space and every other space are structurally
 * incapable of growing a guided step: they carry no recognition kind at all.
 */
export function nextStepFor(record, opts = {}) {
  if (!record) return null
  const kind = record.kind || ''
  if (kind === COMMUNITY_HERO) return heroStep(record, opts)
  if (kind === GREEK_PURSUIT) return greekStep(record)
  if (kind === CHEER_PARTNERSHIP) return cheerStep(record, opts)
  return null
}

/* ---- the pathway rollup (WHO to act on) ------------------------------ */

/** The step labels, in order, for a space's kind. */
export function pathwayFor(kind, opts = {}) {
  const heroLabel = opts.heroLabel || 'Community Hero'
  if (kind === COMMUNITY_HERO) {
    return [
      { key: 'hero-nominate', step: 1, label: `Needs a ${heroLabel}`, hint: 'no live nomination — start here' },
      { key: 'hero-advance', step: 2, label: 'Nomination to advance', hint: 'nominated — move it to production' },
      { key: 'hero-presented', step: 3, label: 'Presented', hint: 'complete' },
    ]
  }
  if (kind === GREEK_PURSUIT) {
    return [
      { key: 'greek-start', step: 1, label: 'No pursuit open', hint: 'pick a product and start' },
      { key: 'greek-own', step: 2, label: 'Needs a rep', hint: 'open, but nobody is on it' },
      { key: 'greek-win', step: 3, label: 'In pursuit', hint: 'assigned — work it to won' },
      { key: 'greek-won', step: 4, label: 'Won', hint: 'closed won' },
    ]
  }
  // THE CHEER PARTNERSHIP FUNNEL, as a worklist. The first bucket is the whole
  // TAM job — every gym nobody has opened a partnership pursuit against yet.
  if (kind === CHEER_PARTNERSHIP) {
    return [
      { key: 'cheer-pursue', step: 1, label: 'Targets — no pursuit', hint: 'the TAM job: open a partnership pursuit' },
      { key: 'cheer-contract', step: 2, label: 'Needs a signed contract', hint: 'pursuit open — get the paper signed' },
      { key: 'cheer-jewelry', step: 3, label: 'Needs the jewelry program', hint: 'signed — launch the exclusive branded program' },
      { key: 'cheer-championships', step: 4, label: 'Partners — work the titles', hint: 'both halves done — pursue the teams’ championships' },
    ]
  }
  return []
}

/**
 * How many records sit at each step — the "who do I act on" bar above the
 * views. Every record lands in exactly one bucket, so the counts always sum to
 * the filtered universe and the bar can never quietly hide a record.
 */
export function pathwayCounts(records, kind, opts = {}) {
  const steps = pathwayFor(kind, opts)
  if (!steps.length) return []
  const tally = Object.fromEntries(steps.map((s) => [s.key, 0]))
  for (const r of records || []) {
    const next = nextStepFor(r, opts)
    if (next && next.key in tally) tally[next.key]++
  }
  return steps.map((s) => ({ ...s, count: tally[s.key] }))
}
